import { useEffect, useMemo, useRef, useState } from "react";
import Reveal from "../lib/Reveal";

type Slide = {
  src: string;
  title?: string;
  caption?: string;
  alt?: string;
};

const slides: Slide[] = [
  {
    src: "/gallery_2.png",
    title: "Infraestrutura pronta",
    caption: "Rede interna organizada e documentada para o condomínio.",
    alt: "Foto de rack e cabeamento organizado",
  },
  {
    src: "/hero-bg.png",
    title: "Conectividade sem ruído",
    caption: "Telefonia integrada e Internet simétrica nas unidades.",
    alt: "Ambiente corporativo com pessoas conectadas",
  },
  {
    src: "/gallery_3.png",
    title: "Monitoramento 24/7",
    caption: "Câmeras inteligentes e operação com SLA dedicado.",
    alt: "CFTV com telas de monitoramento",
  },
];

export default function GalleryCarousel() {
  const [index, setIndex] = useState(0);
  const timerRef = useRef<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // autoplay: 5s (desliga se prefers-reduced-motion)
  const prefersReduced = useMemo(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches,
    []
  );

  const next = () => setIndex((i) => (i + 1) % slides.length);
  const prev = () => setIndex((i) => (i - 1 + slides.length) % slides.length);

  useEffect(() => {
    if (prefersReduced) return;
    timerRef.current = window.setInterval(next, 5000);
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
    };
  }, [prefersReduced]);

  // pausar no hover/focus
  const pause = () => {
    if (timerRef.current) window.clearInterval(timerRef.current);
    timerRef.current = null;
  };
  const resume = () => {
    if (prefersReduced) return;
    if (!timerRef.current) timerRef.current = window.setInterval(next, 5000);
  };

  // pré-carregar imagens
  useEffect(() => {
    slides.forEach((s) => {
      const img = new Image();
      img.src = s.src;
    });
  }, []);

  return (
    <section id="galeria" className="relative isolate bg-background py-14 md:py-20">
      {/* === GeometricFocusBG (inline) — sem blobs/texturas === */}
      <div
        className="pointer-events-none absolute inset-0 -z-10"
        style={{
          // máscara vertical: evita “linha” dura no topo/base quando encosta nas outras seções
          maskImage:
            "linear-gradient(to bottom, transparent 0%, black 10%, black 90%, transparent 100%)",
          WebkitMaskImage:
            "linear-gradient(to bottom, transparent 0%, black 10%, black 90%, transparent 100%)",
        }}
      >
        {/* Feixe esquerdo */}
        <div
          className="absolute -left-10 top-1/3 h-[120%] w-[120%] -rotate-[10deg]"
          style={{
            background:
              "conic-gradient(from 200deg at 20% 50%, rgba(255,0,100,.35) 0deg, rgba(255,0,100,0) 60deg)",
            filter: "blur(30px)",
            maskImage:
              "linear-gradient(to right, transparent, black 20%, black 70%, transparent)",
            WebkitMaskImage:
              "linear-gradient(to right, transparent, black 20%, black 70%, transparent)",
          }}
        />
        {/* Feixe direito */}
        <div
          className="absolute right-[-15%] top-[55%] h-[120%] w-[120%] rotate-[8deg]"
          style={{
            background:
              "conic-gradient(from 140deg at 80% 40%, rgba(255,77,145,.30) 0deg, rgba(255,77,145,0) 70deg)",
            filter: "blur(34px)",
            maskImage:
              "linear-gradient(to left, transparent, black 15%, black 80%, transparent)",
            WebkitMaskImage:
              "linear-gradient(to left, transparent, black 15%, black 80%, transparent)",
          }}
        />
        {/* Foco radial central (elipse) */}
        <div
          className="absolute left-1/2 top-[68%] -translate-x-1/2 -translate-y-1/2 h-[46rem] w-[60rem]"
          style={{
            background:
              "radial-gradient(60% 40% at 55% 50%, rgba(255,0,100,.30) 0%, rgba(255,0,100,0) 65%)",
            filter: "blur(22px)",
          }}
        />
      </div>
      {/* === /GeometricFocusBG === */}

      <div className="max-w-7xl mx-auto px-4">
        <h3 className="text-2xl md:text-4xl font-extrabold">Galeria</h3>

        <Reveal>
          <div
            ref={containerRef}
            className="relative mt-6 aspect-[16/9] w-full overflow-hidden rounded-2xl ring-1 ring-white/10"
            onMouseEnter={pause}
            onMouseLeave={resume}
            onFocus={pause}
            onBlur={resume}
            role="region"
            aria-roledescription="carousel"
            aria-label="Galeria de imagens"
          >
            {/* Slides empilhados com fade; sem scroll */}
            {slides.map((s, i) => {
              const isActive = i === index;
              return (
                <figure
                  key={i}
                  className={`absolute inset-0 transition-opacity duration-700 ease-out ${
                    isActive ? "opacity-100" : "opacity-0"
                  }`}
                  aria-hidden={!isActive}
                >
                  <img
                    src={s.src}
                    alt={s.alt ?? s.title ?? `Slide ${i + 1}`}
                    className="h-full w-full object-cover"
                    loading={i === 0 ? "eager" : "lazy"}
                  />

                  {/* Overlay gradiente + glass para textos */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />

                  {/* Legendas */}
                  {(s.title || s.caption) && (
                    <figcaption className="absolute bottom-0 left-0 right-0 p-4 sm:p-6">
                      <div className="inline-flex max-w-[90%] flex-col rounded-xl bg-black/30 backdrop-blur-md ring-1 ring-white/15 px-4 py-3">
                        {s.title && (
                          <span className="text-white font-semibold text-base sm:text-lg leading-tight">
                            {s.title}
                          </span>
                        )}
                        {s.caption && (
                          <span className="mt-1 text-white/85 text-sm sm:text-base leading-snug">
                            {s.caption}
                          </span>
                        )}
                      </div>
                    </figcaption>
                  )}
                </figure>
              );
            })}

            {/* Controles */}
            <div className="pointer-events-none absolute inset-0 flex items-center justify-between p-2 sm:p-3">
              <button
                type="button"
                onClick={prev}
                className="pointer-events-auto inline-flex h-10 w-10 items-center justify-center rounded-full bg-black/35 hover:bg-black/50 ring-1 ring-white/20 focus:outline-none focus:ring-2 focus:ring-white/40"
                aria-label="Slide anterior"
              >
                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M15 6l-6 6 6 6" />
                </svg>
              </button>
              <button
                type="button"
                onClick={next}
                className="pointer-events-auto inline-flex h-10 w-10 items-center justify-center rounded-full bg-black/35 hover:bg-black/50 ring-1 ring-white/20 focus:outline-none focus:ring-2 focus:ring-white/40"
                aria-label="Próximo slide"
              >
                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 6l6 6-6 6" />
                </svg>
              </button>
            </div>

            {/* Indicadores (bolinhas) */}
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2">
              <div className="flex items-center gap-2 bg-black/25 backdrop-blur-sm rounded-full px-2 py-1 ring-1 ring-white/15">
                {slides.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setIndex(i)}
                    className={`h-2.5 w-2.5 rounded-full transition-all ${
                      index === i ? "bg-white w-6" : "bg-white/50 hover:bg-white/70"
                    }`}
                    aria-label={`Ir para o slide ${i + 1}`}
                    aria-current={index === i ? "true" : "false"}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Acessibilidade: descrição do slide atual para leitores de tela */}
          <p className="sr-only" aria-live="polite">
            Slide {index + 1} de {slides.length}: {slides[index]?.title ?? "imagem"}
          </p>
        </Reveal>
      </div>
    </section>
  );
}
